// Lets the paywall tracker know a paywall impression was reached because the
// Navigator agent navigated the user there (e.g. "do scheduling" -> redirect
// to /scheduling -> the scheduling paywall fires), not because the user
// clicked into it unassisted. Without this, an agent-driven paywall is
// indistinguishable from an organic one: the impression's own trigger only
// knows *where* it fired, never that the agent put the user there.
//
// sessionStorage, not localStorage: this is a same-tab, right-now signal
// about the current navigation, not identity that should span tabs or
// survive into a later session.
//
// Guarded exactly like paywall-analytics.js -- storage access can throw
// (quota, blocked storage in some browser configurations), and analytics must
// never break a render or a navigation.
const STORAGE_KEY = 'paywallAgentOrigin'

// How long a marker stays eligible to attach to the next impression. Short on
// purpose: the whole point is to capture *this* navigation's paywall, not
// whatever the user happens to open a minute later. A wrong attribution is
// worse than a missing one, so this errs short.
const FRESHNESS_MS = 60 * 1000

// Called right before the agent-driven `navigate(...)`, so the marker is in
// place before the destination page's paywall (if any) can mount.
export const markAgentNavigation = (origin = 'navigator_agent') => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof sessionStorage === 'undefined') return
    // eslint-disable-next-line no-undef
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ origin, at: Date.now() }))
  } catch (error) {
    // Analytics must never break a navigation.
  }
}

// Reads and unconditionally clears the marker -- consumed at most once,
// whether or not it turns out to be fresh -- so a stale marker, or one
// already used by an earlier impression, can never silently attach itself to
// a later, unrelated paywall. Returns the origin string only when it is
// still within FRESHNESS_MS; otherwise null.
export const consumeFreshAgentOrigin = () => {
  try {
    // eslint-disable-next-line no-undef
    if (typeof sessionStorage === 'undefined') return null
    // eslint-disable-next-line no-undef
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    // eslint-disable-next-line no-undef
    sessionStorage.removeItem(STORAGE_KEY)
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed.origin !== 'string' || typeof parsed.at !== 'number') return null
    if (Date.now() - parsed.at > FRESHNESS_MS) return null
    return parsed.origin
  } catch (error) {
    return null
  }
}
